import { LDClient, LDContext, initialize } from 'launchdarkly-js-client-sdk';

import { LAUNCH_DARKLY_CLIENT_ID } from '@/constants/environment';

declare global {
  interface Window {
    /** Exposed so reactive hooks can subscribe to `change:<flag>` events. */
    cliengo_ld_client?: LDClient;
  }
}

/** The minimum identity hsm-frontend can supply (from the Cliengo JWT). */
export interface LdIdentity {
  userId: string;
  companyId: string;
}

/**
 * Builds the LaunchDarkly context. SDK 3.x uses the "contexts" model: with
 * `kind: 'user'` the targeting attributes must live at the root of the context
 * (nesting them under `custom`, the legacy LDUser shape, makes flag rules miss
 * and fall through to the default).
 *
 * `companyId` is the critical attribute: `feature-segments` is rolled out
 * per-company, so sending the same `companyId` rapidash sends is what keeps a
 * company enabled in both apps.
 */
export const buildLdContext = ({
  userId,
  companyId,
}: LdIdentity): LDContext => ({
  kind: 'user',
  key: userId,
  companyId,
});

class LaunchDarklyClientSingleton {
  private static instance: LaunchDarklyClientSingleton | undefined;
  private readonly client: LDClient;

  private constructor(identity: LdIdentity) {
    const client = initialize(
      LAUNCH_DARKLY_CLIENT_ID,
      buildLdContext(identity)
    );

    window.cliengo_ld_client = client;
    this.client = client;
  }

  public static getInstance(): LaunchDarklyClientSingleton | undefined {
    return LaunchDarklyClientSingleton.instance;
  }

  public static async initInstance(identity: LdIdentity): Promise<boolean> {
    if (!LaunchDarklyClientSingleton.instance) {
      LaunchDarklyClientSingleton.instance = new LaunchDarklyClientSingleton(
        identity
      );
      await LaunchDarklyClientSingleton.instance.client.waitUntilReady();
    }

    return true;
  }

  public evaluateFlag<T = boolean>(flag: string, defaultValue: T): T {
    return this.client.variation(flag, defaultValue) as T;
  }
}

/** Returns the initialized client, or `undefined` if init has not run yet. */
export const getLDClient = (): LaunchDarklyClientSingleton | undefined =>
  LaunchDarklyClientSingleton.getInstance();

/**
 * Initializes LaunchDarkly once, after auth resolves the JWT identity. No-op
 * when the client-side ID is missing so the app still boots without LD.
 */
export const initLaunchDarkly = async (
  identity: LdIdentity
): Promise<boolean> => {
  if (!LAUNCH_DARKLY_CLIENT_ID) {
    return false;
  }

  return LaunchDarklyClientSingleton.initInstance(identity);
};
